import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  ChartNoAxesCombined, CircleGauge, FileSpreadsheet, Settings2, Truck, Wrench, Warehouse,
} from 'lucide-react';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import ROUTE from 'src/utils/routeConstants';
import TyreOverviewPage from './pages/TyreOverviewPage';
import TyreVehiclesPage from './pages/TyreVehiclesPage';
import TyreInventoryPage from './pages/TyreInventoryPage';
import TyreRetreadingPage from './pages/TyreRetreadingPage';
import TyreReportsPage from './pages/TyreReportsPage';
import TyreAnalyticsPage from './pages/TyreAnalyticsPage';
import { ReportingProvider } from './components/ReportingProvider';
import TyreSetupPage from './pages/TyreSetupPage';
import TyreManagementErrorBoundary from './components/TyreManagementErrorBoundary';
import PageBreadcrumb from './components/PageBreadcrumb';
import { Button } from './components/ui/button';
import './tyre-management.css';

const navigation = [
  { label: 'Overview', path: ROUTE.tyreV2Overview, icon: CircleGauge },
  { label: 'Vehicles', path: ROUTE.tyreV2Vehicles, icon: Truck },
  { label: 'Tyres & Inventory', path: ROUTE.tyreV2Inventory, icon: Warehouse },
  { label: 'Re-treading', path: ROUTE.tyreV2Retreading, icon: Wrench },
  { label: 'Reports', path: ROUTE.tyreV2Reports, icon: FileSpreadsheet },
  { label: 'Analytics', path: ROUTE.tyreV2Analytics, icon: ChartNoAxesCombined },
];

const tyreCrumbs = (pathname, navItems) => {
  const active = navItems.find((item) => (
    pathname === item.path || pathname.startsWith(`${item.path}/`)
  ));
  const onOverview = !active || active.path === ROUTE.tyreV2Overview;
  return [
    { label: 'Vehicle Management' },
    { label: 'Tyre Management', href: onOverview ? undefined : ROUTE.tyreV2Overview },
    ...(onOverview ? [] : [{ label: active.label }]),
  ];
};

const TyreManagementContent = () => {
  const location = useLocation();
  const { getUser } = useAuth();
  const isSuperAdmin = isAdminUser(getUser);
  const items = isSuperAdmin
    ? [...navigation, { label: 'Setup', path: ROUTE.tyreV2Setup, icon: Settings2 }]
    : navigation;

  if (location.pathname === ROUTE.tyreV2Vehicles) {
    return (
      <div className="tyre-management-v2 tm-focus-root h-[100dvh] min-h-0 overflow-hidden">
        <Helmet><title>Tyre workspace | Retrack FMS</title></Helmet>
        <TyreManagementErrorBoundary>
          <TyreVehiclesPage />
        </TyreManagementErrorBoundary>
      </div>
    );
  }

  return (
    <div className="tyre-management-v2 tm-canvas min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8">
      <Helmet><title>Tyre Management | Retrack FMS</title></Helmet>
      <div className="mx-auto max-w-[1600px]">
        <PageBreadcrumb className="mb-3" items={tyreCrumbs(location.pathname, items)} />
        <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 shadow-sm backdrop-blur">
          <div className="flex flex-col gap-4 border-b border-slate-200 px-5 py-5 lg:flex-row lg:items-center lg:justify-between lg:px-7">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20"><CircleGauge className="h-5 w-5" /></span>
              <div><p className="text-lg font-extrabold tracking-tight text-slate-950">Tyre Management</p><p className="text-xs font-semibold text-slate-500">Fleet tyre operations</p></div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="outline" size="sm"><Link to={ROUTE.vehicles}><Truck /> Vehicle List</Link></Button>
              <div className="flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary"><span className="h-2 w-2 rounded-full bg-primary" /> Distance tracking active</div>
            </div>
          </div>
          <nav className="tm-scrollbar flex gap-1 overflow-x-auto px-3 py-2" aria-label="Tyre Management navigation">
            {items.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <Link key={item.path} to={item.path} aria-current={active ? 'page' : undefined} className={`flex shrink-0 items-center gap-2 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'}`}>
                  <Icon className="h-4 w-4" />{item.label}
                </Link>
              );
            })}
          </nav>
        </header>

        <TyreManagementErrorBoundary>
          <Switch>
            <Route exact path={ROUTE.tyreManagementV2}><Redirect to={ROUTE.tyreV2Overview} /></Route>
            <Route exact path={ROUTE.tyreV2Overview} component={TyreOverviewPage} />
            <Route exact path={ROUTE.tyreV2Vehicles} component={TyreVehiclesPage} />
            <Route exact path={[ROUTE.tyreV2Inventory, ROUTE.tyreV2InventoryScan]} component={TyreInventoryPage} />
            <Route exact path={ROUTE.tyreV2Retreading} component={TyreRetreadingPage} />
            <Route exact path={ROUTE.tyreV2Alerts}>
              <Redirect to={`${ROUTE.notifications}?target_model=tyrealert`} />
            </Route>
            <Route exact path={ROUTE.tyreV2Reports} component={TyreReportsPage} />
            <Route exact path={ROUTE.tyreV2Analytics} component={TyreAnalyticsPage} />
            {isSuperAdmin && <Route exact path={ROUTE.tyreV2Setup} component={TyreSetupPage} />}
            <Redirect to={ROUTE.tyreV2Overview} />
          </Switch>
        </TyreManagementErrorBoundary>
      </div>
    </div>
  );
};

const TyreManagementV2 = () => {
  const { pathname } = useLocation();
  const activeView = pathname === ROUTE.tyreV2Reports ? 'reports' : pathname === ROUTE.tyreV2Analytics ? 'analytics' : null;
  return <ReportingProvider activeView={activeView}><TyreManagementContent /></ReportingProvider>;
};
export default TyreManagementV2;
